// Sugalarm — frame variant detection (trinary, cycle 25 + cycle 26 + cycle 39).
//
// Resolution precedence (top wins):
//   0. The OFFICIAL native kiosk shell (`isNativeKioskShell()` — the
//      device-owner Android kiosk APK we sell + provision, detected by a
//      kiosk-flavor-only Capacitor plugin) → "kiosk". This is the ONLY
//      hardware that earns the full-screen layout from a tablet form
//      factor. It sits at the top so it wins over the Capacitor-native →
//      "mobile" step below (the APK is Capacitor-native too).
//   0b. ANY OTHER tablet-class device — iPad (cycle 52) OR a non-official
//      Android tablet (cycle 193) — is DEMOTED to "mobile" in BOTH
//      orientations (cycle 267 product policy). Previously (cycle 52/193)
//      a tablet in landscape rendered the kiosk layout; now a random
//      iPad / store-bought tablet (browser OR app) keeps the mobile-phone
//      look-and-feel — a full-screen experience requires the provisioned
//      kiosk hardware above. Placed ABOVE the explicit-flag step so a
//      `?kiosk=1` flag set by hand in a tablet browser can't unlock
//      full-screen either. Phones never match the tablet predicates
//      (shorter-side ≥ 700 px floor), so their behavior is unchanged; the
//      Pi panel is not tablet-class (Chromium, no Android UA, 600 px
//      short side) so it sails past this to the flag / MQ steps below.
//   1. Explicit kiosk flag (`isKiosk()`), gated by runtime:
//        - If the runtime is phone-like (`isPhoneLikeRuntime()` true),
//          AUTO-CLEAR the flag via `setKiosk(false)` and fall through.
//          This is the cycle-39 stuck-on-a-phone recovery path: a
//          phone is never a kiosk, so a sticky `sugalarm.kiosk = "1"`
//          from a stale bookmark / autofill is presumed accidental.
//          Auto-clearing also makes the fix sticky on the next visit
//          instead of forcing the user to type `?kiosk=0`.
//        - Otherwise → "kiosk".
//   2. Capacitor native runtime → "mobile". Sits above the kiosk MQ
//      so a native shell on a small device never reads as kiosk.
//   3. Phone signals — ANY of: viewport ≤ 600px wide, display-mode
//      standalone, iOS `navigator.standalone === true`, OR
//      `isPhoneLikeRuntime()` true (cycle 39 — catches iPhone in
//      landscape and Android phones with viewports > 600px wide).
//      → "mobile".
//   4. Pi-mounted Chromium media query: matches both
//      `(max-width: 1024px) and (max-height: 640px)` AND
//      `(pointer: fine)`. The `pointer: fine` clause (cycle 39) is
//      the long-term defense against phones in landscape painting
//      kiosk — the real Pi panel reports `pointer: fine` because
//      Chromium kiosk mode treats the framebuffer as a mouse-driven
//      surface even with touch attached. → "kiosk".
//   5. Otherwise → "web".
//
// `isCapacitorNative()` lives in `lib/capacitor.ts` — the single owner
// of `window.Capacitor` access. `isPhoneLikeRuntime()` lives in
// `lib/deviceClass.ts` — the single owner of the UA / pointer /
// maxTouchPoints heuristic. Do not duplicate either check elsewhere.

import { useEffect, useState } from "react";

import { isKiosk, setKiosk } from "../lib/auth";
import { isCapacitorNative, isNativeKioskShell } from "../lib/capacitor";
import {
  isAndroidTabletClassRuntime,
  isIPadClassRuntime,
  isPhoneLikeRuntime,
} from "../lib/deviceClass";

export type FrameVariant = "kiosk" | "web" | "mobile";

const KIOSK_QUERY = "(max-width: 1024px) and (max-height: 640px)";
const MOBILE_WIDTH_QUERY = "(max-width: 600px)";
const STANDALONE_QUERY = "(display-mode: standalone)";
const POINTER_FINE_QUERY = "(pointer: fine)";
const PORTRAIT_QUERY = "(orientation: portrait)";

function isIOSStandalone(): boolean {
  if (typeof window === "undefined") return false;
  // iOS Safari's `navigator.standalone` is the canonical A2HS signal —
  // there's no matching media query that fires inside Safari proper.
  return (window.navigator as { standalone?: boolean }).standalone === true;
}

function compute(): FrameVariant {
  if (typeof window === "undefined") return "web";

  // 0. The OFFICIAL native kiosk shell (the provisioned Android kiosk
  //    APK) → "kiosk". The only tablet-form hardware that earns it.
  if (isNativeKioskShell()) return "kiosk";

  // 0b. Cycle 267: every OTHER tablet-class device (a random iPad or a
  //     non-official Android tablet, browser OR store app) keeps the
  //     mobile-phone layout in BOTH orientations — full-screen requires
  //     the provisioned kiosk hardware above. (Was cycle 52/193:
  //     landscape → kiosk.)
  if (isIPadClassRuntime() || isAndroidTabletClassRuntime()) {
    return "mobile";
  }

  // 1. Explicit kiosk flag, gated by runtime.
  if (isKiosk()) {
    if (isPhoneLikeRuntime()) {
      // Stuck-on-a-phone recovery: clear and fall through.
      setKiosk(false);
    } else {
      return "kiosk";
    }
  }

  // 2. Capacitor native runtime → "mobile" before any size query.
  if (isCapacitorNative()) return "mobile";

  // 3. Phone signals.
  if (
    window.matchMedia(MOBILE_WIDTH_QUERY).matches ||
    window.matchMedia(STANDALONE_QUERY).matches ||
    isIOSStandalone() ||
    isPhoneLikeRuntime()
  ) {
    return "mobile";
  }

  // 4. Pi-mounted Chromium media query (now AND-gated on pointer: fine).
  if (
    window.matchMedia(KIOSK_QUERY).matches &&
    window.matchMedia(POINTER_FINE_QUERY).matches
  ) {
    return "kiosk";
  }

  // 5. Default.
  return "web";
}

export function useFrameVariant(): FrameVariant {
  const [variant, setVariant] = useState<FrameVariant>(compute);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const kioskMQ = window.matchMedia(KIOSK_QUERY);
    const mobileMQ = window.matchMedia(MOBILE_WIDTH_QUERY);
    const standaloneMQ = window.matchMedia(STANDALONE_QUERY);
    const pointerFineMQ = window.matchMedia(POINTER_FINE_QUERY);
    // Cycle 52: an iPad rotating between landscape and portrait must
    // re-resolve (kiosk ↔ mobile) without a reload.
    const portraitMQ = window.matchMedia(PORTRAIT_QUERY);
    const onChange = () => setVariant(compute());
    kioskMQ.addEventListener("change", onChange);
    mobileMQ.addEventListener("change", onChange);
    standaloneMQ.addEventListener("change", onChange);
    pointerFineMQ.addEventListener("change", onChange);
    portraitMQ.addEventListener("change", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      kioskMQ.removeEventListener("change", onChange);
      mobileMQ.removeEventListener("change", onChange);
      standaloneMQ.removeEventListener("change", onChange);
      pointerFineMQ.removeEventListener("change", onChange);
      portraitMQ.removeEventListener("change", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  return variant;
}
